<template>
  <div class="container position-sticky z-index-sticky top-0">
    <navbar
      :dark-mode="true"
      is-blur="blur border-radius-lg my-3 py-2 start-0 end-0 mx-4 shadow"
    />
  </div>
  <main class="main-content mt-0">
    <div class="page-header min-vh-100">
      <span class="mask bg-gradient-dark opacity-6"></span>
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-6 col-md-8 col-sm-10 mx-auto text-center">
            <div class="card z-index-0 fadeIn3 fadeInBottom">
              <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                <div class="bg-gradient-primary shadow-primary border-radius-lg py-3 pe-1">
                  <h4 class="text-white font-weight-bolder text-center mt-2 mb-0">
                    Cerrando Sesión Remota
                  </h4>
                </div>
              </div>
              
              <div class="card-body">
                <!-- Loading State -->
                <div v-if="loading" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
                    <span class="sr-only">Cargando...</span>
                  </div>
                  <p class="mt-3 text-muted">Verificando token y cerrando sesión...</p>
                  <p class="mb-0 text-secondary">
                    Procesando solicitud de cierre de sesión...
                  </p>
                </div>

                <!-- Success State -->
                <div v-else-if="success" class="text-center py-4">
                  <div class="text-center mb-4">
                    <i class="fas fa-check-circle text-success" style="font-size: 4rem;"></i>
                  </div>
                  <h3 class="text-success mb-3">¡Sesión Cerrada Exitosamente!</h3>
                  <div class="alert alert-success border-0" role="alert">
                    <strong>{{ message }}</strong>
                  </div>
                  <p class="text-muted mb-4">
                    Tu sesión anterior ha sido cerrada exitosamente. 
                    Ahora puedes iniciar sesión en tu nuevo dispositivo.
                  </p>
                  <argon-button 
                    color="primary" 
                    variant="gradient"
                    size="lg"
                    class="w-100"
                    @click="redirectToLogin"
                  >
                    <i class="fas fa-sign-in-alt me-2"></i>
                    Ir al Login
                  </argon-button>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="text-center py-4">
                  <div class="text-center mb-4">
                    <i class="fas fa-exclamation-triangle text-danger" style="font-size: 4rem;"></i>
                  </div>
                  <h3 class="text-danger mb-3">Error al Cerrar Sesión</h3>
                  
                  <!-- Validation Errors -->
                  <div v-if="validationErrors" class="mb-4">
                    <div class="alert alert-danger border-0" role="alert">
                      <strong>{{ message }}</strong>
                    </div>
                    <div class="text-start">
                      <h6 class="text-dark mb-2">Detalles del error:</h6>
                      <ul class="list-unstyled">
                        <li v-for="(errorList, field) in validationErrors" :key="field" class="text-danger mb-1">
                          <strong>{{ field }}:</strong>
                          <span v-for="(error, index) in errorList" :key="index" class="d-block">
                            • {{ error }}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  <!-- General Error -->
                  <div v-else class="mb-4">
                    <div class="alert alert-danger border-0" role="alert">
                      <strong>{{ message }}</strong>
                    </div>
                  </div>

                  <div class="d-grid gap-2">
                    <argon-button 
                      color="primary" 
                      variant="gradient"
                      size="lg"
                      class="w-100"
                      @click="redirectToLogin"
                    >
                      <i class="fas fa-sign-in-alt me-2"></i>
                      Ir al Login
                    </argon-button>
                    <argon-button 
                      v-if="token && email" 
                      color="warning"
                      variant="outline"
                      size="lg"
                      class="w-100 mt-2"
                      @click="retry"
                    >
                      <i class="fas fa-redo me-2"></i>
                      Reintentar
                    </argon-button>
                  </div>
                </div>

                <!-- Missing Parameters -->
                <div v-else class="text-center py-4">
                  <div class="text-center mb-4">
                    <i class="fas fa-exclamation-triangle text-warning" style="font-size: 4rem;"></i>
                  </div>
                  <h3 class="text-warning mb-3">Enlace Inválido</h3>
                  <div class="alert alert-warning border-0" role="alert">
                    <strong>El enlace que seguiste no es válido o está incompleto.</strong>
                  </div>
                  <p class="text-muted mb-4">
                    Verifica que hayas copiado la URL completa del email que recibiste.
                  </p>
                  <argon-button 
                    color="primary" 
                    variant="gradient"
                    size="lg"
                    class="w-100"
                    @click="redirectToLogin"
                  >
                    <i class="fas fa-sign-in-alt me-2"></i>
                    Ir al Login
                  </argon-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
  <app-footer />
</template>

<script>
import { onBeforeMount, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import Navbar from "@/examples/PageLayout/Navbar.vue";
import AppFooter from "@/examples/PageLayout/Footer.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import authService from "@/services/auth.service";

export default {
  name: "CloseSession",
  components: {
    Navbar,
    AppFooter,
    ArgonButton,
  },
  setup() {
    const store = useStore();
    const toggleDefaultLayout = () => store.commit("toggleDefaultLayout");
    
    onBeforeMount(() => {
      store.state.hideConfigButton = true;
      toggleDefaultLayout();
      document.getElementsByTagName("body")[0].classList.remove("bg-gray-100");
    });
    
    onBeforeUnmount(() => {
      store.state.hideConfigButton = false;
      toggleDefaultLayout();
    });
  },
  data() {
    return {
      loading: false,
      success: false,
      error: false,
      message: "",
      validationErrors: null,
      token: null,
      email: null,
    };
  },
  async mounted() {
    // Capturar parámetros de la URL
    this.token = this.$route.query.token;
    this.email = this.$route.query.email;

    // Si tenemos ambos parámetros, proceder con el cierre de sesión
    if (this.token && this.email) {
      await this.closeRemoteSession();
    } else {
      // Mostrar error si faltan parámetros
      this.error = true;
      this.message = "Faltan parámetros requeridos (token y email) en la URL.";
    }
  },
  methods: {
    async closeRemoteSession() {
      this.loading = true;
      this.error = false;
      this.success = false;
      this.validationErrors = null;

      try {
        const response = await authService.closeRemoteSession(this.token, this.email);
        
        // Éxito
        this.success = true;
        this.message = response.message || "Sesión cerrada exitosamente.";
        
        // Redireccionar automáticamente después de 3 segundos
        setTimeout(() => {
          this.redirectToLogin();
        }, 3000);

      } catch (error) {
        this.error = true;
        
        if (error.response) {
          const status = error.response.status;
          const data = error.response.data;

          switch (status) {
            case 422:
              // Errores de validación
              this.message = data.message || "Datos inválidos.";
              this.validationErrors = data.errors || null;
              break;
              
            case 400:
              // Error de solicitud incorrecta
              this.message = data.message || "Solicitud incorrecta. Verifica los datos enviados.";
              break;
              
            case 500:
              // Error interno del servidor
              this.message = data.message || "Error interno del servidor. Intenta nuevamente más tarde.";
              break;
              
            default:
              // Otros errores HTTP
              this.message = data.message || `Error ${status}: ${error.response.statusText}`;
          }
        } else if (error.request) {
          // Error de red
          this.message = "Error de conexión. Verifica tu conexión a internet e intenta nuevamente.";
        } else {
          // Otros errores
          this.message = error.message || "Ocurrió un error inesperado.";
        }
      } finally {
        this.loading = false;
      }
    },
    
    async retry() {
      if (this.token && this.email) {
        await this.closeRemoteSession();
      }
    },
    
    redirectToLogin() {
      this.$router.push({ name: "Login" });
    },
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.spinner-border {
  border-width: 0.25em;
}

.alert {
  border-radius: 0.75rem;
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.card {
  box-shadow: 0 20px 27px rgba(0, 0, 0, 0.05);
  border: 0;
}

.card-header {
  border-bottom: none;
}

.btn {
  border-radius: 0.5rem;
}

.fadeIn3 {
  animation: fadeIn 0.5s ease-in;
}

.fadeInBottom {
  animation: fadeInBottom 0.6s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInBottom {
  from { 
    opacity: 0;
    transform: translateY(30px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}

.text-center i {
  margin-bottom: 1rem;
}
</style>